import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowOutward from '@mui/icons-material/ArrowOutward';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import './auth.css';

export function AuthPasswordField(props) {
  const { t } = useTranslation('auth');
  const [visible, setVisible] = useState(false);
  return <TextField {...props} type={visible ? 'text' : 'password'} fullWidth
    InputProps={{ endAdornment: <InputAdornment position="end">
      <IconButton type="button" aria-label={t(visible ? 'hidePassword' : 'showPassword')}
        aria-pressed={visible} onClick={() => setVisible((value) => !value)} edge="end">
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment> }} />;
}

export default function AuthLayout({ title, subtitle, children, onToggleTheme }) {
  const { t, i18n } = useTranslation('auth');
  const theme = useTheme();
  const { pathname } = useLocation();
  const registration = pathname === '/register';
  const authTabs = pathname === '/login' || registration;
  return <div className="auth-shell" data-theme={theme.palette.mode}>
    <a className="auth-skip" href="#auth-form">{t('skip')}</a>
    <header className="auth-header">
      <Link className="auth-brand" to="/" aria-label={t('home')}>
        <img src={`${process.env.PUBLIC_URL}/favicon.svg?v=20261027`} width="32" height="32" alt="" aria-hidden="true" />Soccertycs
      </Link>
      <div className="auth-controls">
        <div className="auth-language" role="group" aria-label={t('language')}>
          {['es', 'en'].map((language) => <button type="button" key={language} lang={language}
            aria-label={language === 'es' ? 'Español' : 'English'}
            aria-pressed={(i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2) === language}
            onClick={() => i18n.changeLanguage(language)}>{language.toUpperCase()}</button>)}
        </div>
        {onToggleTheme && <IconButton onClick={onToggleTheme}
          aria-label={t(theme.palette.mode === 'dark' ? 'lightTheme' : 'darkTheme')}>
          {theme.palette.mode === 'dark' ? <LightModeOutlined /> : <DarkModeOutlined />}
        </IconButton>}
        <Link className="auth-home" to="/"><ArrowBack aria-hidden="true" /><span>{t('backHome')}</span></Link>
      </div>
    </header>
    <main className="auth-main">
      <aside className="auth-story" aria-label={t('storyLabel')}>
        <div className="auth-story-intro">
          <span className="auth-eyebrow">{t('eyebrow')}</span>
          <p className="auth-statement">{t('statement')}<br /><span>{t('statementAccent')}</span></p>
          <p className="auth-story-copy">{t(registration ? 'registrationStory' : 'loginStory')}</p>
        </div>
        <div className="auth-pitch" aria-hidden="true">
          <svg viewBox="0 0 560 310" fill="none">
            <rect x="30" y="25" width="500" height="260" rx="4" />
            <path d="M280 25v260M30 90h75v130H30m500-130h-75v130h75M30 120h30v70H30m500-70h-30v70h30" />
            <circle cx="280" cy="155" r="49" />
            <path className="auth-play-line" d="M150 230 225 180 310 205 400 115" />
            <path className="auth-play-arrow" d="m380 115 20 0 0 20" />
            <circle className="auth-player" cx="150" cy="230" r="9" />
            <circle className="auth-player" cx="225" cy="180" r="9" />
            <circle className="auth-player" cx="310" cy="205" r="9" />
            <circle className="auth-player auth-player-end" cx="400" cy="115" r="15" />
            <circle className="auth-opponent" cx="170" cy="115" r="7" />
            <circle className="auth-opponent" cx="320" cy="80" r="7" />
            <circle className="auth-opponent" cx="420" cy="235" r="7" />
          </svg>
          <span className="auth-pitch-caption">{t('pitchCaption')}<ArrowOutward /></span>
        </div>
        <div className="auth-story-footer">
          <span>{t('competition')}</span><span>{t('video')}</span><span>{t('scouting')}</span>
        </div>
      </aside>
      <section className="auth-form-panel" id="auth-form" aria-labelledby="auth-title" tabIndex={-1}>
        {authTabs && <nav className="auth-tabs" aria-label={t('accountNavigation')}>
          <Link to="/login" aria-current={!registration ? 'page' : undefined}>{t('loginTab')}</Link>
          <Link to="/register" aria-current={registration ? 'page' : undefined}>{t('registerTab')}</Link>
        </nav>}
        <div className="auth-form-heading">
          <span className="auth-eyebrow">{t(registration ? 'registerEyebrow' : 'loginEyebrow')}</span>
          <h1 id="auth-title">{title}</h1>
          <p>{subtitle}</p>
        </div>
        {children}
        <p className="auth-footnote">{t('footnote')}</p>
      </section>
    </main>
  </div>;
}
